import { call, put, takeEvery, takeLatest, select } from 'redux-saga/effects';
import api from '@control-front-end/common/sagas/api';
import AppUtils from '@control-front-end/utils/utils';
import {
  SHOW_NOTIFY,
  NOTIFY_LEVEL,
  MANAGE_ACCESS_RULES,
  RequestStatus,
} from 'constants';
import {
  GET_SCRIPTS,
  GET_SCRIPT,
  SEARCH_SCRIPTS,
  CREATE_SCRIPT,
  UPDATE_SCRIPT,
  UPDATE_SCRIPT_ENV,
  REMOVE_SCRIPT,
  SCRIPT_ACCESS,
  DEPLOY_SCRIPT,
  SET_SCRIPTS_REQ_STATUS,
} from '../constants/scripts';

/**
 * Создать модель справочника
 */
function* makeScriptModel(models) {
  const config = yield select((state) => state.config);
  models = Array.isArray(models) ? models : [models];
  for (const model of models) {
    if (model.picture) {
      model.pictureUrl = AppUtils.makeAppUrl(`/download/${model.picture}`);
    }
    model.ownerAvatar = config.avatarPath.replace('{userId}', model.ownerId);
    if (!model.access) {
      model.access = [];
    }
    // eslint-disable-next-line no-restricted-syntax
    for (const a of model.access) {
      a.avatar = config.avatarPath.replace('{userId}', a.userId);
    }
  }
}

/**
 * Редактирование настроек доступа
 */
function* manageAccess(script, access) {
  const accessActions = access.filter(({ action }) => !!action);
  const oldAccess = script.access || [];
  const newAccess = AppUtils.getManagedAccess(oldAccess, accessActions);
  if (newAccess.length) {
    yield put({
      type: MANAGE_ACCESS_RULES.REQUEST,
      payload: { body: newAccess, objId: script.id, objType: 'application' },
    });
  }
}

/**
 * Изменить права доступа на слой
 */
function* updateScriptAccess({ payload }) {
  const { scriptId, access } = payload;
  const { list } = yield select((state) => state.scripts);
  const newList = list.slice();
  const index = newList.findIndex((i) => i.id === scriptId);
  newList.splice(index, 1, { ...list[index], access });
  yield put({
    type: SCRIPT_ACCESS.SUCCESS,
    payload: { list: newList },
  });
}

/**
 * Получить список приложений CDU
 */
function* getScripts({ payload, callback }) {
  const { loadMore, localState } = payload;
  let scriptsState;
  if (scriptsState) {
    scriptsState = localState;
  } else {
    scriptsState = yield select((state) => state.scripts);
  }
  const { reqStatus, list, limit, offset, endList } = scriptsState;
  if (reqStatus !== RequestStatus.SUCCESS || endList) return;
  // Ставим флаг прогресса загрузки
  yield put({ type: SET_SCRIPTS_REQ_STATUS, payload: 'inProgress' });
  const accounts = yield select((state) => state.accounts);
  const accId = accounts.active;
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/applications/list/${accId}`,
    queryParams: { limit, offset },
  });
  // Ставим флаг окончания загрузки
  yield put({ type: SET_SCRIPTS_REQ_STATUS, payload: 'success' });
  if (result !== RequestStatus.SUCCESS) return;
  yield call(makeScriptModel, data.data);
  const newScriptsList = loadMore ? list.concat(data.data) : data.data;
  const newPayload = {
    list: newScriptsList,
    offset: offset + limit,
    endList: !data.data.length,
  };
  if (localState && callback) {
    callback({ ...localState, ...newPayload });
    return;
  }
  yield put({
    type: GET_SCRIPTS.SUCCESS,
    payload: newPayload,
  });
  if (callback) callback(newScriptsList);
}

/**
 * Поиск приложений CDU
 */
function* searchScripts({ payload, callback }) {
  const { query } = payload;
  const accounts = yield select((state) => state.accounts);
  const accId = accounts.active;
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/applications/search/${accId}?q=${encodeURIComponent(query)}`,
  });
  if (result !== RequestStatus.SUCCESS) return;
  yield call(makeScriptModel, data.data);
  if (callback) callback(data.data);
  yield put({
    type: SEARCH_SCRIPTS.SUCCESS,
    payload: {
      list: data.data,
    },
  });
}

/**
 * Получить приложение CDU
 */
function* getScript({ payload: scriptId, callback, errorCallback }) {
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/applications/${scriptId}`,
    handleErrCodes: [403],
  });
  if (result === RequestStatus.SUCCESS) {
    yield call(makeScriptModel, data.data);
    yield put({
      type: GET_SCRIPT.SUCCESS,
      payload: {
        list: [data.data],
      },
    });
    if (callback) callback(data.data);
  } else if (errorCallback && data.statusCode === 403) {
    errorCallback();
  }
}

/**
 * Удалить приложение
 */
function* removeScript({ payload, callback }) {
  const { scriptId } = payload;
  const { result } = yield call(api, {
    method: 'delete',
    url: `/applications/${scriptId}`,
  });
  if (result !== RequestStatus.SUCCESS) return;
  const { list } = yield select((state) => state.scripts);
  const index = list.findIndex((i) => i.id === scriptId);
  if (index === -1) return;
  const newList = list.slice();
  newList.splice(index, 1);
  yield put({
    type: REMOVE_SCRIPT.SUCCESS,
    payload: { list: newList },
  });
  if (callback) callback();
}

/**
 * Создать приложение CDU
 */
function* createScript({ payload, formActions, callback }) {
  const {
    title,
    shortName,
    picture,
    description,
    sharedWith,
    corezoidCredentials,
    access,
  } = payload;
  const accounts = yield select((state) => state.accounts);
  const accId = accounts.active;
  const { list } = yield select((state) => state.scripts);
  const { result, data } = yield call(api, {
    method: 'post',
    url: `/applications/${accId}`,
    body: {
      title,
      shortName,
      picture,
      description,
      sharedWith,
      corezoidCredentials,
    },
  });
  if (result !== RequestStatus.SUCCESS) {
    if (formActions) formActions.setSubmitting(false);
    return;
  }
  const newScript = { ...data.data };
  yield call(makeScriptModel, newScript);
  // Добавить права на скрипт
  if (access && access.length) {
    yield call(manageAccess, newScript, access);
  }
  const newList = list.slice();
  newList.unshift(newScript);
  yield put({
    type: CREATE_SCRIPT.SUCCESS,
    payload: { list: newList },
  });
  if (formActions && typeof formActions.callback === 'function') {
    formActions.callback({ id: newScript.id });
  }
  if (callback) callback(data.data);
}

/**
 * Обновить окружение приложения CDU
 */
function* updateScriptEnv({ payload, formActions, callback }) {
  const { scriptId, envId, credentials } = payload;
  const { list } = yield select((state) => state.scripts);
  const { result, data } = yield call(api, {
    method: 'put',
    url: `/applications/env/${scriptId}/${envId}`,
    body: credentials,
  });
  if (result !== RequestStatus.SUCCESS) {
    if (formActions) formActions.setSubmitting(false);
    return;
  }
  const copyList = list.slice();
  const index = copyList.findIndex((i) => i.id === scriptId);
  if (index !== -1) {
    const copyScript = copyList[index];
    const envIndex = copyScript.envs.findIndex((i) => i.id === envId);
    const oldEnv = copyScript.envs[envIndex];
    copyScript.envs.splice(envIndex, 1, {
      ...oldEnv,
      ...data.data.corezoidCredentials,
    });
    copyList.splice(index, 1, copyScript);
    yield put({
      type: UPDATE_SCRIPT_ENV.SUCCESS,
      payload: { list: copyList },
    });
  }
  if (formActions) formActions.onClose();
  if (callback) callback(data.data);
}

/**
 * Деплой приложения CDU
 */
function* deployScript({ payload, callback }) {
  const { scriptId, sourceEnvId, targetEnvId } = payload;
  const { result, data } = yield call(api, {
    method: 'post',
    url: `/applications/deploy/${scriptId}`,
    body: { sourceEnvId, targetEnvId },
  });
  if (result !== RequestStatus.SUCCESS) return;
  yield put({
    type: SHOW_NOTIFY.REQUEST,
    payload: {
      id: AppUtils.createRid(),
      type: NOTIFY_LEVEL.SUCCESS,
      label: 'Deployment completed successfully.',
    },
  });
  if (callback) callback(data.data);
}

/**
 * Обновить приложение CDU
 */
function* updateScript({ payload, callback }) {
  const { id, title, shortName, picture, description, sharedWith, access } =
    payload;
  const { list } = yield select((state) => state.scripts);
  const { result, data } = yield call(api, {
    method: 'put',
    url: `/applications/${id}`,
    body: { title, shortName, picture, description, sharedWith },
  });
  if (result !== RequestStatus.SUCCESS) return;
  const copyList = structuredClone(list);
  const index = copyList.findIndex((i) => i.id === id);
  if (index !== -1) {
    const copyScript = copyList[index];
    yield call(makeScriptModel, data.data);
    // Добавить права на скрипт
    if (access && access.length) {
      yield call(manageAccess, data.data, access);
    }
    copyList.splice(index, 1, { ...copyScript, ...data.data });
    yield put({
      type: UPDATE_SCRIPT.SUCCESS,
      payload: { list: copyList },
    });
  }
  if (callback) callback(data.data);
}

function* scripts() {
  yield takeEvery(GET_SCRIPTS.REQUEST, getScripts);
  yield takeEvery(SEARCH_SCRIPTS.REQUEST, searchScripts);
  yield takeEvery(GET_SCRIPT.REQUEST, getScript);
  yield takeEvery(DEPLOY_SCRIPT.REQUEST, deployScript);
  yield takeLatest(REMOVE_SCRIPT.REQUEST, removeScript);
  yield takeLatest(CREATE_SCRIPT.REQUEST, createScript);
  yield takeLatest(UPDATE_SCRIPT.REQUEST, updateScript);
  yield takeLatest(UPDATE_SCRIPT_ENV.REQUEST, updateScriptEnv);
  yield takeLatest(SCRIPT_ACCESS.REQUEST, updateScriptAccess);
}

export default scripts;
